import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  makeStyles,
  shorthands,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Spinner,
} from "@fluentui/react-components";
import React, { useContext, useEffect, useState } from "react";
import { useYouTrackProjectsWhenOpen } from "../../hooks/useYouTrackProjectsWhenOpen";
import UserContext from "../../modules/AppRouter/UserContext";
import usersService from "../../services/users.service";
import type { IUserProjectAssignment } from "../../services/types/users";
import type { IYouTrackProjectOption } from "../../services/youtrackProjects.service";
import { buildUserProjectSettingsPayload } from "../../utils/userProjectSettings";
import { formatYouTrackProjectLabel } from "../../utils/youtrackProject";
import appErrorToast from "./AppErrorToast";
import AppButton from "./AppButton";

interface IUserManageProjectsDialogProps {
  userId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const useStyles = makeStyles({
  fieldLabel: {
    display: "block",
    marginBottom: "4px",
    marginTop: "12px",
    fontWeight: 600,
  },
  triggerButton: {
    width: "100%",
    maxWidth: "400px",
    justifyContent: "flex-start",
  },
  popoverSurface: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("6px"),
    minWidth: "240px",
    maxWidth: "400px",
    maxHeight: "200px",
    overflowY: "auto",
  },
});

const SELECT_PROJECTS_PLACEHOLDER = "Wybierz projekty";

function buildSavedProjectNamesMap(
  worksIn: IUserProjectAssignment[],
  manages: IUserProjectAssignment[],
): Record<string, string> {
  const names: Record<string, string> = {};
  for (const project of [...worksIn, ...manages]) {
    names[project.youTrackProjectId] = project.projectName;
  }
  return names;
}

function getButtonLabel(
  selectedIds: string[],
  placeholder: string,
  savedProjectNames: Record<string, string>,
  youTrackProjects: IYouTrackProjectOption[],
): string {
  if (selectedIds.length === 0) {
    return placeholder;
  }
  return selectedIds
    .map((id) => {
      const project = youTrackProjects.find((p) => p.id === id);
      if (project) {
        return formatYouTrackProjectLabel(project.shortName, project.name);
      }
      return savedProjectNames[id] ?? id;
    })
    .join(", ");
}

function getTriggerButtonLabel(
  popoverOpen: boolean,
  frozenLabel: string,
  selectedIds: string[],
  savedProjectNames: Record<string, string>,
  youTrackProjects: IYouTrackProjectOption[],
): string {
  return popoverOpen
    ? frozenLabel
    : getButtonLabel(
        selectedIds,
        SELECT_PROJECTS_PLACEHOLDER,
        savedProjectNames,
        youTrackProjects,
      );
}

const UserManageProjectsDialog: React.FC<IUserManageProjectsDialogProps> = ({
  userId,
  open,
  setOpen,
}) => {
  const styles = useStyles();
  const { userData, refreshUser } = useContext(UserContext);
  const youTrackProjects = useYouTrackProjectsWhenOpen(open);
  const [worksInSelected, setWorksInSelected] = useState<string[]>([]);
  const [managesSelected, setManagesSelected] = useState<string[]>([]);
  const [worksInPopoverOpen, setWorksInPopoverOpen] = useState(false);
  const [managesPopoverOpen, setManagesPopoverOpen] = useState(false);
  const [worksInFrozenLabel, setWorksInFrozenLabel] = useState(SELECT_PROJECTS_PLACEHOLDER);
  const [managesFrozenLabel, setManagesFrozenLabel] = useState(SELECT_PROJECTS_PLACEHOLDER);
  const [hasIncompleteWorkHours, setHasIncompleteWorkHours] = useState(false);
  const [savedProjectNames, setSavedProjectNames] = useState<Record<string, string>>({});
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setWorksInSelected([]);
      setManagesSelected([]);
      setWorksInPopoverOpen(false);
      setManagesPopoverOpen(false);
      setHasIncompleteWorkHours(false);
      setSavedProjectNames({});
      setIsLoadingSettings(false);
      setIsSaving(false);
      return;
    }

    if (!userId) {
      return;
    }

    let cancelled = false;
    setIsLoadingSettings(true);

    usersService
      .getUserProjectSettings(userId)
      .then((settings) => {
        if (cancelled) {
          return;
        }
        setWorksInSelected(
          settings.worksInProjects.map((project) => project.youTrackProjectId),
        );
        setManagesSelected(
          settings.managesProjects.map((project) => project.youTrackProjectId),
        );
        setHasIncompleteWorkHours(settings.hasIncompleteWorkHours);
        setSavedProjectNames(
          buildSavedProjectNamesMap(settings.worksInProjects, settings.managesProjects),
        );
      })
      .catch(() => {
        if (!cancelled) {
          appErrorToast("Nie udało się pobrać ustawień projektów pracownika.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSettings(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const handleWorksInPopoverOpenChange = (_: unknown, data: { open?: boolean }) => {
    const isOpen = Boolean(data.open);
    if (isOpen) {
      setWorksInFrozenLabel(
        getButtonLabel(
          worksInSelected,
          SELECT_PROJECTS_PLACEHOLDER,
          savedProjectNames,
          youTrackProjects,
        ),
      );
    }
    setWorksInPopoverOpen(isOpen);
  };

  const handleManagesPopoverOpenChange = (_: unknown, data: { open?: boolean }) => {
    const isOpen = Boolean(data.open);
    if (isOpen) {
      setManagesFrozenLabel(
        getButtonLabel(
          managesSelected,
          SELECT_PROJECTS_PLACEHOLDER,
          savedProjectNames,
          youTrackProjects,
        ),
      );
    }
    setManagesPopoverOpen(isOpen);
  };

  const toggleSelection = (
    projectId: string,
    checked: boolean,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setSelected((prev) =>
      checked ? [...prev, projectId] : prev.filter((id) => id !== projectId),
    );
  };

  const handleSave = () => {
    if (!userId || isLoadingSettings || isSaving) {
      return;
    }

    const payload = buildUserProjectSettingsPayload(
      worksInSelected,
      managesSelected,
      hasIncompleteWorkHours,
      youTrackProjects,
      savedProjectNames,
    );

    setIsSaving(true);
    usersService
      .updateUserProjectSettings(userId, payload)
      .then(async () => {
        if (userId && userData?.data?.id && userId === userData.data.id) {
          await refreshUser();
        }
        setOpen(false);
      })
      .catch(() => appErrorToast("Nie udało się zapisać ustawień projektów pracownika."))
      .finally(() => setIsSaving(false));
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Zarządzaj projektami</DialogTitle>
          <DialogContent>
            {isLoadingSettings ? (
              <Spinner size="medium" label="Ładowanie ustawień…" />
            ) : (
              <>
            <Label className={styles.fieldLabel}>Projekty, w których pracuje</Label>
            <Popover
              open={worksInPopoverOpen}
              onOpenChange={handleWorksInPopoverOpenChange}
              positioning={{
                position: "below",
                align: "start",
                autoSize: "width",
              }}
            >
              <PopoverTrigger disableButtonEnhancement>
                <Button className={styles.triggerButton}>
                  {getTriggerButtonLabel(
                    worksInPopoverOpen,
                    worksInFrozenLabel,
                    worksInSelected,
                    savedProjectNames,
                    youTrackProjects,
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverSurface className={styles.popoverSurface}>
                {youTrackProjects.map((project) => {
                  const label = formatYouTrackProjectLabel(project.shortName, project.name);
                  return (
                    <Checkbox
                      key={project.id}
                      label={label}
                      checked={worksInSelected.includes(project.id)}
                      disabled={managesSelected.includes(project.id)}
                      onChange={(_, data) =>
                        toggleSelection(project.id, Boolean(data.checked), setWorksInSelected)
                      }
                    />
                  );
                })}
              </PopoverSurface>
            </Popover>

            <Label className={styles.fieldLabel}>Projekty, którymi zarządza</Label>
            <Popover
              open={managesPopoverOpen}
              onOpenChange={handleManagesPopoverOpenChange}
              positioning="below-start"
            >
              <PopoverTrigger disableButtonEnhancement>
                <Button className={styles.triggerButton}>
                  {getTriggerButtonLabel(
                    managesPopoverOpen,
                    managesFrozenLabel,
                    managesSelected,
                    savedProjectNames,
                    youTrackProjects,
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverSurface className={styles.popoverSurface}>
                {youTrackProjects.map((project) => {
                  const label = formatYouTrackProjectLabel(project.shortName, project.name);
                  return (
                    <Checkbox
                      key={project.id}
                      label={label}
                      checked={managesSelected.includes(project.id)}
                      disabled={worksInSelected.includes(project.id)}
                      onChange={(_, data) =>
                        toggleSelection(project.id, Boolean(data.checked), setManagesSelected)
                      }
                    />
                  );
                })}
              </PopoverSurface>
            </Popover>

            <Label className={styles.fieldLabel}>Niepełne godziny pracy</Label>
            <RadioGroup
              layout="horizontal"
              value={hasIncompleteWorkHours ? "true" : "false"}
              onChange={(_, data) => setHasIncompleteWorkHours(data.value === "true")}
            >
              <Radio value="true" label="Tak" />
              <Radio value="false" label="Nie" />
            </RadioGroup>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Anuluj</Button>
            </DialogTrigger>
            <AppButton
              text="Zapisz"
              appearance="primary"
              disabled={isLoadingSettings || isSaving || !userId}
              onClick={handleSave}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UserManageProjectsDialog;
